import type { StaffHomeTrends } from "@lafayette/schemas";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useOptionalHomeRuntime } from "./runtime";

// The read behind the Home stat tiles: `GET /staff/home-trends`. It holds counts only, so it needs no more care than the attention summary:
// it stays in memory while Home uses it (`gcTime: 0`) and is read again every few minutes while the tab is visible. A failed read is not
// retried by the library: Home says the numbers could not be loaded and offers a retry. A runtime with no trends reader draws no tiles.

const TRENDS_REFRESH_MS = 5 * 60_000;

const TRENDS_KEY = ["home-trends"] as const;

export function useHomeTrends() {
  const trends = useOptionalHomeRuntime()?.trends;
  return useQuery<StaffHomeTrends>({
    queryKey: TRENDS_KEY,
    queryFn: ({ signal }) => (trends as NonNullable<typeof trends>).trends({ signal }),
    enabled: trends !== undefined,
    gcTime: 0,
    staleTime: 60_000,
    retry: false,
    retryOnMount: false,
    refetchInterval: TRENDS_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

/** Reads the numbers again now. A page with no trends reader has no active query, so this does nothing there. */
export function useRefreshHomeTrends(): () => Promise<void> {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: TRENDS_KEY });
}
